<template>
  <form @submit.prevent="onSubmit">
    <div class="flex items-baseline mb-2">
      <label
        for="avatar"
        class="block mb-1 px-0.5 text-sm font-medium select-none text-gray-800/70"
        >Your Avatar
      </label>
      <fa-icon
        v-if="!showEdit"
        icon="pencil"
        class="ml-2 text-xs cursor-pointer text-gray-800/70 hover:text-gray-800 transition-colors"
        title="Edit avatar"
        @click.prevent="toggleEditMode" />
      <span
        v-if="showEdit"
        class="ml-2 text-xs cursor-pointer hover:underline text-gray-800/70 select-none"
        @click.prevent="toggleEditMode">
        cancel
      </span>
    </div>

    <div class="flex flex-col items-center space-y-2">
      <div v-if="showEdit" class="w-full max-w-md">
        <!-- Edit options -->
        <div class="flex justify-center items-center mb-3">
          <div class="relative min-w-0 size-16 md:size-32">
            <!-- Image preview if in upload mode -->
            <div
              v-if="imagePreview"
              class="overflow-hidden w-full h-full rounded-full">
              <img
                :src="imagePreview"
                alt="Avatar preview"
                class="object-cover w-full h-full" />
            </div>
            <!-- Fallback to current avatar if nothing selected yet -->
            <UserAvatar v-else :user="profile" size="auto" />
          </div>
        </div>

        <!-- Upload avatar option -->
        <div class="space-y-3">
          <!-- File upload area -->
          <div
            class="p-4 text-center border-2 border-indigo-600 border-dashed rounded-md cursor-pointer bg-white/50 hover:bg-white/70"
            :class="{ 'bg-blue-50/70 border-blue-500': isDragging }"
            @click="triggerFileInput"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
            <div class="space-y-1">
              <fa-icon icon="cloud-arrow-up" class="text-3xl text-gray-400" />
              <p class="text-sm text-gray-800">
                Click to upload or drag and drop
              </p>
              <p class="text-xs italic text-gray-700">PNG, JPG, HEIC or GIF</p>
            </div>
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleFileUpload" />
          <button
            v-if="imagePreview"
            class="text-xs text-red-500 hover:text-red-700"
            @click.prevent="clearUpload">
            Remove image
          </button>
        </div>
      </div>

      <div
        v-else
        class="min-w-0 size-16 md:size-32 cursor-pointer"
        @click="toggleEditMode">
        <UserAvatar :user="profile" size="auto" />
      </div>

      <template v-if="showEdit">
        <button
          v-if="!formBusy"
          type="submit"
          class="px-4 w-20 h-8 text-xs font-bold text-white bg-indigo-600 rounded-md cursor-pointer hover:bg-indigo-500"
          @click.prevent="onSubmit">
          Save
        </button>
        <button
          v-else
          class="px-4 w-20 h-8 text-xs font-bold text-white bg-gray-400 rounded-md cursor-pointer cursor-not-allowed">
          <fa-icon icon="spinner-third" spin />
        </button>
      </template>
    </div>

    <div
      v-if="formError"
      class="p-2 mt-2 text-xs text-red-500 rounded-md cursor-pointer select-none bg-white/50 hover:bg-white/70"
      @click.prevent="formError = null">
      <fa-icon icon="triangle-exclamation" /> Failed to update avatar
      <p class="font-bold">
        <span v-if="formError.message">{{ formError.message }}</span>
        <span v-else>{{ formError }}</span>
      </p>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStoreProfile } from '@/stores/storeProfile'
import UserAvatar from '@/components/UserAvatar.vue'

const storeProfile = useStoreProfile()
const props = defineProps({
  profile: Object,
  autoEdit: {
    type: Boolean,
    default: false
  }
})

const showEdit = ref(false)
const formBusy = ref(false)
const formError = ref(null)
const fileInput = ref(null)
// const avatarType = ref('upload') - removed, only upload supported now
const imageFile = ref(null) // Processed file for upload
const originalImageFile = ref(null) // Original uploaded file
const imagePreview = ref(null)
// Color avatar options removed
const isDragging = ref(false)

// Color options removed - only image upload supported

// Preview user object removed - only image upload supported

onMounted(() => {
  // Initialize with current avatar settings - only upload supported
  // If they have an existing avatar URL, show it in the preview
  if (props.profile?.avatarUrl) {
    imagePreview.value = props.profile.avatarUrl
  }

  // Auto-enable edit mode if autoEdit prop is true
  if (props.autoEdit) {
    showEdit.value = true
    formError.value = null
  }
})

// Avatar type watch removed - only upload supported

// Replace the existing toggleEditMode function with this:
const toggleEditMode = () => {
  if (showEdit.value) {
    cancelEditMode()
  } else {
    showEdit.value = true
    formError.value = null

    // Initialize edit mode with current profile settings - only upload supported
    if (props.profile?.avatarUrl) {
      imagePreview.value = props.profile.avatarUrl
    }
  }
}

const cancelEditMode = () => {
  showEdit.value = false
  formError.value = null
  imagePreview.value = props.profile?.avatarUrl || null
  imageFile.value = null
}

const triggerFileInput = () => {
  fileInput.value.click()
}

// Process and resize image using canvas
// This handles HEIC conversion and large file sizes
const processImage = (file, maxSize = 800) => {
  return new Promise((resolve, reject) => {
    // Create a blob URL for the file
    const blobUrl = URL.createObjectURL(file)
    const img = new Image()

    img.onload = () => {
      URL.revokeObjectURL(blobUrl)

      try {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img
        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width)
            width = maxSize
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width * maxSize) / height)
            height = maxSize
          }
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        // Convert to JPEG blob (handles HEIC conversion automatically)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create a new File from the blob
              const processedFile = new File([blob], 'avatar.jpg', {
                type: 'image/jpeg'
              })
              resolve({
                file: processedFile,
                previewUrl: canvas.toDataURL('image/jpeg', 0.9)
              })
            } else {
              reject(new Error('Failed to process image'))
            }
          },
          'image/jpeg',
          0.9
        )
      } catch (err) {
        reject(err)
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(blobUrl)
      reject(new Error('Failed to load image'))
    }

    img.src = blobUrl
  })
}

const handleFileUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  // Clear previous errors
  formError.value = null

  // Check if it's an image (loose check to allow HEIC and other formats)
  if (!file.type.startsWith('image/')) {
    formError.value = { message: 'Please select an image file' }
    return
  }

  // Warn if file is very large (over 50MB) - likely an error
  if (file.size > 50 * 1024 * 1024) {
    formError.value = {
      message: 'File is too large. Please select a smaller image.'
    }
    return
  }

  try {
    // Show loading state
    formBusy.value = true

    // Store original file
    originalImageFile.value = file

    // Process the image (resize and convert to JPEG) for preview
    const { file: processedFile, previewUrl } = await processImage(file, 800)

    imageFile.value = processedFile // Use processed file for upload
    imagePreview.value = previewUrl
  } catch (error) {
    console.error('Error processing image:', error)
    formError.value = {
      message: 'Failed to process image. Please try a different photo.'
    }
  } finally {
    formBusy.value = false
  }
}

const clearUpload = () => {
  imageFile.value = null
  originalImageFile.value = null
  imagePreview.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Color selection functions removed

const onDragOver = (event) => {
  isDragging.value = true
  // Some browsers require a dataTransfer effect to be set to allow dropping
  event.dataTransfer.dropEffect = 'copy'
}

const onDragLeave = () => {
  isDragging.value = false
}

const onDrop = async (event) => {
  isDragging.value = false

  // Get the dropped files
  const files = event.dataTransfer.files
  if (files.length === 0) return

  // Use only the first file if multiple files are dropped
  const file = files[0]

  // Clear previous errors
  formError.value = null

  // Check if it's an image (loose check to allow HEIC and other formats)
  if (!file.type.startsWith('image/')) {
    formError.value = { message: 'Please select an image file' }
    return
  }

  // Warn if file is very large (over 50MB) - likely an error
  if (file.size > 50 * 1024 * 1024) {
    formError.value = {
      message: 'File is too large. Please select a smaller image.'
    }
    return
  }

  try {
    // Show loading state
    formBusy.value = true

    // Store original file
    originalImageFile.value = file

    // Process the image (resize and convert to JPEG) for preview
    const { file: processedFile, previewUrl } = await processImage(file, 800)

    imageFile.value = processedFile // Use processed file for upload
    imagePreview.value = previewUrl
  } catch (error) {
    console.error('Error processing image:', error)
    formError.value = {
      message: 'Failed to process image. Please try a different photo.'
    }
  } finally {
    formBusy.value = false
  }
}

const updateAvatar = async () => {
  formBusy.value = true

  try {
    let avatarData = {}

    if (imageFile.value) {
      // Process image to create multiple sizes
      const originalFile = imageFile.value

      // Create regular sized version (400x400)
      const regularBlob = await createSizedAvatar(originalFile, 400)
      const regularFile = new File([regularBlob], 'avatar.jpg', {
        type: 'image/jpeg'
      })

      // Create small version (100x100)
      const smallBlob = await createSizedAvatar(originalFile, 100)
      const smallFile = new File([smallBlob], 'avatar-sm.jpg', {
        type: 'image/jpeg'
      })

      avatarData = {
        avatarType: 'image',
        originalFile: originalImageFile.value, // Original uploaded file
        avatarFile: regularFile,
        avatarFileSm: smallFile
      }
    }

    await storeProfile.updateAvatar(avatarData)
    formBusy.value = false
    toggleEditMode()
  } catch (error) {
    formBusy.value = false
    formError.value = error
  }
}

// Helper function to create sized avatar
const createSizedAvatar = (file, size) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)

      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')

      // Draw image centered and cropped to square
      const minDim = Math.min(img.width, img.height)
      const sourceX = (img.width - minDim) / 2
      const sourceY = (img.height - minDim) / 2

      ctx.drawImage(img, sourceX, sourceY, minDim, minDim, 0, 0, size, size)

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to create blob'))
          }
        },
        'image/jpeg',
        0.9
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }

    img.src = url
  })
}

const onSubmit = (e) => {
  e.preventDefault()

  // Check if image was uploaded
  if (imageFile.value) {
    updateAvatar()
  } else {
    toggleEditMode()
  }
}

// Function to save without toggling edit mode (for onboarding)
const saveForOnboarding = async () => {
  if (!props.autoEdit) return

  // Check if image was uploaded
  if (!imageFile.value) return

  try {
    formBusy.value = true

    // Process image to create multiple sizes
    const originalFile = imageFile.value

    // Create regular sized version (400x400)
    const regularBlob = await createSizedAvatar(originalFile, 400)
    const regularFile = new File([regularBlob], 'avatar.jpg', {
      type: 'image/jpeg'
    })

    // Create thumbnail version (100x100)
    const thumbBlob = await createSizedAvatar(originalFile, 100)
    const thumbFile = new File([thumbBlob], 'avatar_thumb.jpg', {
      type: 'image/jpeg'
    })

    const avatarData = {
      avatarType: 'image',
      avatarFile: regularFile,
      avatarThumbFile: thumbFile
    }

    await storeProfile.updateAvatar(avatarData)
    formBusy.value = false
  } catch (error) {
    formBusy.value = false
    formError.value = error
    throw error
  }
}

// Check if there are changes
const hasChanges = () => {
  if (!props.autoEdit) return false
  return !!imageFile.value
}

// Expose methods for parent component
defineExpose({
  saveForOnboarding,
  hasChanges
})
</script>
